// src/components/common/EmployeeNameLink.jsx
import React, { useState } from 'react';
import { useEmployeeModal } from '../../contexts/EmployeeModalContext';

/**
 * Renders an employee name as a subtle clickable link.
 * Keeps the surrounding color by default; turns teal + underline on hover.
 *
 * Props:
 *   employee   — object with at minimum { employee_id }; also accepts { name } or { first_name, last_name }
 *   tab        — TABS constant for which tab to open (default 0 = DIR)
 *   children   — override display text (defaults to derived employee name)
 *   style      — additional inline styles applied to the outer span
 */
const EmployeeNameLink = ({ employee, tab = 0, children, style = {} }) => {
  const { openEmployeeModal } = useEmployeeModal();
  const [hovered, setHovered] = useState(false);

  const isClickable = String(employee?.employee_id || '').startsWith('EMP');

  const handleClick = (e) => {
    e.stopPropagation();
    if (isClickable) openEmployeeModal(employee, tab);
  };

  const displayName = children
    ?? (employee?.first_name && employee?.last_name
      ? `${employee.first_name} ${employee.last_name}`
      : employee?.name ?? 'Employee');

  if (!isClickable) return <span style={style}>{displayName}</span>;

  return (
    <span
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        color: hovered ? '#03DAC6' : 'inherit',
        textDecoration: hovered ? 'underline' : 'none',
        ...style,
      }}
    >
      {displayName}
    </span>
  );
};

export default EmployeeNameLink;

/**
 * Lightweight clickable name for plain-style (non-MUI) components.
 * Accepts an `onClick(name)` callback directly — no context needed.
 * Renders as plain text when onClick is not provided.
 */
export const ClickableName = ({ name, onClick, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  if (!onClick) return <span style={style}>{name}</span>;
  return (
    <span
      onClick={(e) => { e.stopPropagation(); onClick(name); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        color: hovered ? '#03DAC6' : 'inherit',
        textDecoration: hovered ? 'underline' : 'none',
        ...style,
      }}
    >
      {name}
    </span>
  );
};
